<script setup>
import { ref } from 'vue';
import { defineEmits } from 'vue';
import axios from '@/plugins/axios.js';
import { useAlerts } from '@/store/alerts.js';
import KiwishLoader from './KiwishLoader.vue';

const VUE_ASSETS_URL = process.env.VUE_APP_ASSETS_URL;
const APP_NAME = process.env.VUE_APP_NAME;

const emit = defineEmits(['close']);

const contactData = ref({
  email: '',
  message: ''
});

const sending = ref(false);
const errors = ref({});

const closeModal = () => {
  emit('close');
  resetForm();
};

const resetForm = () => {
  contactData.value = {
    email: '',
    message: ''
  };
  errors.value = {};
};

const sendContact = async () => {
  // Validación básica
  errors.value = {};

  if (!contactData.value.email || contactData.value.email.trim() === '') {
    errors.value.email = 'contact.error.email.required';
    useAlerts().alert = {visible: true, message: 'msg.form.error', class: 'alert-error'};
    return;
  }

  if (!validateEmail(contactData.value.email)) {
    errors.value.email = 'contact.error.email.invalid';
    useAlerts().alert = {visible: true, message: 'msg.form.error', class: 'alert-error'};
    return;
  }

  if (!contactData.value.message || contactData.value.message.trim() === '') {
    errors.value.message = 'contact.error.message.required';
    useAlerts().alert = {visible: true, message: 'msg.form.error', class: 'alert-error'};
    return;
  }

  sending.value = true;

  try {
    const response = await axios.post('/contact', contactData.value);

    if (response.status === 200) {
      useAlerts().alert = {visible: true, message: 'contact.success', class: 'alert-success'};
      closeModal();
    }
  } catch (e) {
    if (e.response?.data?.errors) {
      const { errors: formErrors } = e.response.data;
      errors.value = Object.keys(formErrors).reduce((acc, key) => {
        acc[key] = formErrors[key][0];
        return acc;
      }, {});
    }
    useAlerts().alert = {visible: true, message: e.response?.data?.message || 'contact.error.general', class: 'alert-error'};
  } finally {
    sending.value = false;
  }
};

const validateEmail = (email) => {
  const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return re.test(email);
};

// Validación en tiempo real (onBlur)
const validateEmailOnBlur = () => {
  if (!contactData.value.email || contactData.value.email.trim() === '') {
    errors.value.email = 'contact.error.email.required';
  } else if (!validateEmail(contactData.value.email)) {
    errors.value.email = 'contact.error.email.invalid';
  }
};

const validateMessageOnBlur = () => {
  if (!contactData.value.message || contactData.value.message.trim() === '') {
    errors.value.message = 'contact.error.message.required';
  }
};
</script>

<template>
  <div class="contact-page">
    <!-- Header con logo y botón cerrar -->
    <div class="contact-header">
      <button class="close-btn" @click="closeModal">
        <ion-icon name="close-outline"></ion-icon>
      </button>
      <img :src="VUE_ASSETS_URL + 'logokw_dark.png'"
           :srcset="VUE_ASSETS_URL + 'logokw_dark.png 1x, ' + VUE_ASSETS_URL + 'logokw_dark@2.png 2x'"
           alt="Kiwish"
           class="contact-logo" />
    </div>

    <!-- Contenido -->
    <div class="contact-content">
      <div class="ion-text-center">
        <ion-text class="contact-title">{{ $t('contact.title') }}</ion-text>
      </div>

      <div class="ion-text-center mt-3">
        <ion-text class="contact-subtitle">{{ $t('contact.subtitle') }}</ion-text>
      </div>

      <div class="contact-input mt-4">
        <ion-input
          type="email"
          v-model="contactData.email"
          :placeholder="$t('contact.field.email.placeholder')"
          @keydown="errors.email = ''"
          @blur="validateEmailOnBlur"
          aria-describedby="email-error"
        />
      </div>
      <ion-text id="email-error" class="error-text" role="alert" v-if="errors?.email">{{ $t(errors.email) }}</ion-text>

      <div class="contact-input contact-textarea mt-3">
        <ion-textarea
          v-model="contactData.message"
          maxlength="500"
          :placeholder="$t('contact.field.message.placeholder')"
          rows="5"
          @keydown="errors.message = ''"
          @blur="validateMessageOnBlur"
          aria-describedby="message-error"
        />
      </div>
      <ion-text id="message-error" class="error-text" role="alert" v-if="errors?.message">{{ $t(errors.message) }}</ion-text>

      <div class="contact-buttons mt-6">
        <button class="btn-send" @click="sendContact" :disabled="sending">
          {{ $t('contact.send') }}
        </button>
      </div>
    </div>

    <KiwishLoader :is-open="sending" :message="$t('contact.sending')" />
  </div>
</template>

<style scoped>
/* ============================================
   CONTACT PAGE - NUEVO DISEÑO
   ============================================ */

.contact-page {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
}

/* Header con logo */
.contact-header {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding-top: calc(40px + env(safe-area-inset-top, 0px));
  padding-bottom: 20px;
  position: relative;
}

.contact-logo {
  width: 120px;
  height: auto;
}

/* Botón cerrar */
.close-btn {
  position: absolute;
  right: 20px;
  top: calc(20px + env(safe-area-inset-top, 0px));
  background: transparent;
  border: none;
  padding: 8px;
  cursor: pointer;
}

.close-btn ion-icon {
  font-size: 1.8rem;
  color: #5A695B;
}

/* Contenido */
.contact-content {
  padding: 0 28px;
  flex: 1;
  overflow-y: auto;
}

.contact-title {
  font-size: 1.4rem;
  font-weight: 600;
  color: #5A695B;
}

.contact-subtitle {
  color: #8A9A8B;
  line-height: 1.4;
  font-size: 0.95rem;
}

/* Inputs estilo registro */
.contact-input {
  background-color: #FFFFFF;
  border: 1px solid #5A695B;
  border-radius: 17px;
  height: 53px;
  padding: 0 20px;
  display: flex;
  align-items: center;
}

.contact-input ion-input {
  --background: transparent;
  --padding-start: 0;
  --padding-end: 0;
  --placeholder-color: #FF5287;
  --placeholder-opacity: 0.3;
  color: #5A695B;
  flex: 1;
}

.contact-textarea {
  height: 140px;
  padding: 12px 20px;
  align-items: flex-start;
}

.contact-textarea ion-textarea {
  --background: transparent;
  --padding-start: 0;
  --padding-end: 0;
  --placeholder-color: #FF5287;
  --placeholder-opacity: 0.3;
  color: #5A695B;
  flex: 1;
  height: 100%;
}

.error-text {
  color: #dc3545;
  font-size: 0.8rem;
  margin-top: 4px;
  margin-left: 8px;
  display: block;
}

/* Botones */
.contact-buttons {
  display: flex;
  justify-content: center;
  margin-top: 30px;
}

.btn-send {
  background: transparent;
  border: 1.5px solid #5A695B;
  border-radius: 10px;
  padding: 12px 50px;
  font-size: 1rem;
  color: #5A695B;
  cursor: pointer;
  transition: all 0.2s ease;
}

.btn-send:active {
  background: rgba(90, 105, 91, 0.1);
}

.btn-send:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Utilidades */
.mt-3 {
  margin-top: 0.75rem;
}

.mt-4 {
  margin-top: 1rem;
}

.mt-6 {
  margin-top: 1.5rem;
}
</style>
